import {
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  makeStyles,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  Tooltip,
  Typography,
} from "@material-ui/core";
import React, { FC } from "react";
import {
  useTranslation,
  withTranslation,
  WithTranslation,
} from "react-i18next";
import { connect } from "react-redux";
import {
  RouteComponentProps,
  useLocation,
  useParams,
  withRouter,
} from "react-router-dom";
import { compose, Dispatch } from "redux";
import { actionShowSpinner } from "../../../../redux-flow/action/Wrapper/Spinner";
import { InfoContainer, SubTitleContainer, TitleContainer } from "./styles";

import { KeyboardDatePicker } from "@material-ui/pickers";
import ReactInputMask from "react-input-mask";
import { useSlotCommitteeDetails } from "../../../../hooks/useSlotCommitteeDetails";
import { getInputDateFormats } from "../../../../utils/get-date-time-locale";
import { Select } from "../../../Common/Select";
import { formatCurrencyInputValue } from "../../utils";
import { getSlotStatus } from "../../utils/getSlotStatus";
import CommitteCard from "../CommitteCard";
import CustomSelect from "./CustomSelect";
import CustomTextField from "./CustomTextField";
import MinuteDeliberationInfo from "./MinuteDeliberationInfo";

interface LocationState {
  slotCommitteeId: string;
}

interface RouteParams {
  id: string;
}

interface SlotCommitteeDetailsProps
  extends RouteComponentProps,
    WithTranslation {}

const SlotCommitteeDetails: FC<SlotCommitteeDetailsProps> = () => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { id: slotId } = useParams<RouteParams>();
  const { slotCommitteeId } = useLocation<LocationState>().state;
  const { slotCommitteeInformations, materialStatus, minuteSlot } =
    useSlotCommitteeDetails(slotId, slotCommitteeId);

  if (!slotCommitteeInformations) return null;

  const complianceLabel = (() => {
    const map: Record<string, string> = {
      na: t("slots.complianceOptions.na"),
      low: t("slots.complianceOptions.low"),
      medium: t("slots.complianceOptions.medium"),
      high: t("slots.complianceOptions.high"),
    };
    return map[slotCommitteeInformations?.complianceOpinion ?? ""] ?? "";
  })();

  return (
    <>
      <Grid container direction="row" spacing={2}>
        <Grid item xs={12} md={12} lg={9}>
          <Paper elevation={1} style={{ padding: 40, marginBottom: 40 }}>
            <TitleContainer>
              <Typography variant="h4">
                {t("slotDetails.slotInformations")}
              </Typography>
            </TitleContainer>

            <InfoContainer>
              <SubTitleContainer>
                <Typography className={classes.subtitle}>
                  {t("slotDetails.generalInformations")}
                </Typography>
                <FormControlLabel
                  disabled={true}
                  control={
                    <Tooltip title={t("slots.extraSlot")}>
                      <Switch
                        checked={slotCommitteeInformations?.extraSlot}
                        name="extraSlot"
                        color="primary"
                      />
                    </Tooltip>
                  }
                  label={t("slots.extraSlot")}
                />
              </SubTitleContainer>

              <Grid container spacing={3}>
                <Grid item lg={4} xs={6}>
                  <CustomTextField
                    id="requester"
                    label="slots.requester"
                    value={slotCommitteeInformations?.requestName}
                  />
                </Grid>
                <Grid item lg={4} xs={6}>
                  <CustomTextField
                    id="board"
                    label="slots.board"
                    value={slotCommitteeInformations?.directoryName}
                  />
                </Grid>

                <Grid item lg={4} xs={6}>
                  <KeyboardDatePicker
                    autoOk
                    disableToolbar
                    className={classes.keyboardDatePicker}
                    variant="inline"
                    format={getInputDateFormats(i18n.language)}
                    id="createdIn"
                    label={t("slots.createdIn")}
                    value={slotCommitteeInformations?.createdDateTime}
                    disabled={true}
                    KeyboardButtonProps={{
                      "aria-label": "change date",
                    }}
                    onChange={() => {}}
                  />
                </Grid>

                <Grid item lg={4} xs={6}>
                  <CustomTextField
                    id="type"
                    label="slots.type"
                    value={t(`slots.slotType.${slotCommitteeInformations?.kind}`, '')}
                  />
                </Grid>

                <Grid item lg={4} xs={6}>
                  <CustomTextField
                    id="theme"
                    label="slots.theme"
                    value={slotCommitteeInformations?.slotThemeName}
                  />
                </Grid>
                <Grid item lg={4} xs={6}>
                  <CustomTextField
                    id="objective"
                    label="slots.objective"
                    value={slotCommitteeInformations?.objective}
                  />
                </Grid>
                <Grid item lg={8} xs={12}>
                  <CustomTextField
                    id="institution"
                    label="slots.institution"
                    value={slotCommitteeInformations?.institution}
                  />
                </Grid>

                <Grid item lg={4} xs={6}>
                  <CustomSelect
                    id="modality"
                    label="slots.modality"
                    options={slotCommitteeInformations?.modalities?.map(
                      (mod) => ({
                        id: mod.modalityId,
                        name: mod.modalityName,
                      })
                    )}
                    value={slotCommitteeInformations?.modalities?.map(
                      (mod) => ({
                        id: mod.modalityId,
                        name: mod.modalityName,
                      })
                    )}
                  />
                </Grid>

                <Grid item lg={4} xs={6}>
                  <CustomTextField
                    id="materialStatus"
                    label="slots.statusMaterial"
                    value={materialStatus}
                  />
                </Grid>

                <Grid item lg={4} xs={6}>
                  <CustomTextField
                    id="projectIdentifier"
                    label="menu.slot.projectIdentifier"
                    value={slotCommitteeInformations?.projectIdentifier}
                  />
                </Grid>

                <Grid item lg={4} xs={6}>
                  <CustomSelect
                    id="companies"
                    label="slots.companies"
                    options={slotCommitteeInformations?.companies?.map(
                      (company) => ({
                        id: company.companyId,
                        name: company.companyName,
                      })
                    )}
                    value={slotCommitteeInformations?.companies?.map(
                      (company) => ({
                        id: company.companyId,
                        name: company.companyName,
                      })
                    )}
                  />
                </Grid>

                <Grid container >
                  <Grid item lg={4} xs={6}>
                    <CustomTextField
                      id="complianceOpinion"
                      label="slots.complianceOpinion"
                      value={complianceLabel}
                    />
                  </Grid>
                </Grid>

                <SubTitleContainer>
                  <Typography className={classes.subtitle}>
                    {t("slots.budget.financialDetails")}
                  </Typography>
                </SubTitleContainer>

                <Grid container >
                  <Grid item lg={4} xs={6}>
                    <CustomTextField
                      id="currency"
                      label="menu.slot.currency"
                      value={slotCommitteeInformations?.currency}
                    />
                  </Grid>

                  <Grid item lg={8} xs={12}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">
                        {t("slots.budget.title")}
                      </FormLabel>

                      <RadioGroup
                        aria-label="budget"
                        name="budget"
                        value={slotCommitteeInformations?.budgeted}
                        row
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio size="small" />}
                          label={t("slots.budget.yes")}
                          disabled
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio size="small" />}
                          label={t("slots.budget.no")}
                          disabled
                        />
                        <FormControlLabel
                          value="na"
                          control={<Radio size="small" />}
                          label={t("slots.budget.na")}
                          disabled
                        />
                        <FormControlLabel
                          value="pno"
                          control={<Radio size="small" />}
                          label={t("slots.budget.pno")}
                          disabled
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>

                <SubTitleContainer>
                  <Typography className={classes.subtitle}>
                    {t("slots.budget.label")}
                  </Typography>
                </SubTitleContainer>

                <Grid container spacing={3}>
                  <Grid item lg={4} xs={6}>
                    <CustomTextField
                      id="amountNotBudgetedYear"
                      label="amountNotBudgetedYear"
                      value={formatCurrencyInputValue(
                        slotCommitteeInformations?.amountNotBudgetedYear?.slice(
                          0,
                          -2
                        )
                      )}
                    />
                  </Grid>
                </Grid>

                <SubTitleContainer>
                  <Typography className={classes.subtitle}>
                    {t("slots.budget.requested")}
                  </Typography>
                </SubTitleContainer>

                <Grid container spacing={3}>
                  <Grid item lg={4} xs={6}>
                    <CustomTextField
                      id="value"
                      label="value"
                      value={formatCurrencyInputValue(
                        slotCommitteeInformations?.value?.slice(0, -2)
                      )}
                    />
                  </Grid>

                  <Grid item lg={4} xs={6}>
                    <CustomTextField
                      id="additive"
                      label="additive"
                      value={formatCurrencyInputValue(
                        slotCommitteeInformations?.additive?.slice(0, -2)
                      )}
                    />
                  </Grid>

                  <Grid item lg={4} xs={6}>
                    <CustomTextField
                      id="approvedValue"
                      label="approvedValue"
                      value={formatCurrencyInputValue(
                        slotCommitteeInformations?.approvedValue?.slice(0, -2)
                      )}
                    />
                  </Grid>

                  <Grid item lg={12} xs={6}>
                    <CustomTextField
                      id="sponsor"
                      label="sponsor"
                      value={slotCommitteeInformations?.sponsor}
                    />
                  </Grid>

                  <Grid item lg={8} xs={6}>
                    <CustomTextField
                      id="caveats"
                      label="slots.caveats"
                      value={slotCommitteeInformations?.caveats}
                    />
                  </Grid>

                  <Grid item lg={4} xs={6}>
                    <ReactInputMask
                      mask="99:99"
                      maskChar=""
                      name="duration"
                      value={slotCommitteeInformations?.duration}
                      disabled
                    >
                      {
                        // @ts-ignore
                        () => (
                          <CustomTextField
                            value={slotCommitteeInformations?.duration}
                            id="duration"
                            label="slots.duration"
                          />
                        )
                      }
                    </ReactInputMask>
                  </Grid>

                  <Grid item lg={4} xs={6}>
                    <KeyboardDatePicker
                      autoOk
                      disableToolbar
                      fullWidth
                      className={classes.keyboardDatePicker}
                      variant="inline"
                      format={getInputDateFormats(i18n.language)}
                      id="meetingDate"
                      label={t("slots.meetingDate")}
                      value={slotCommitteeInformations?.meetDateTime}
                      disabled={true}
                      KeyboardButtonProps={{
                        "aria-label": "change date",
                      }}
                      onChange={() => {}}
                    />
                  </Grid>

                  <Grid item lg={4} xs={6}>
                    <CustomTextField
                      id="statusSlot"
                      label="slots.statusSlot"
                      value={getSlotStatus(
                        slotCommitteeInformations?.status,
                        t
                      )}
                    />
                  </Grid>

                  <Grid item lg={4} xs={6}>
                    <CustomTextField
                      id="slotCreationUser"
                      value={slotCommitteeInformations?.creationUser?.name ?? ``}
                      label={t("slots.creationUser")}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </InfoContainer>
          </Paper>
        </Grid>

        <Grid item lg={3}>
          <Paper elevation={1} style={{ padding: 20, marginBottom: 10 }}>
            <Grid item>
              <Typography
                className={classes.subtitle}
                style={{ marginTop: "77px" }}
              >
                {t("slots.presenter")}
              </Typography>

              <Grid item lg={12} xs={12}>
                <FormControlLabel
                  disabled
                  control={
                    <Checkbox
                      disabled
                      value={slotCommitteeInformations?.anyoneCanHost}
                      checked={slotCommitteeInformations?.anyoneCanHost}
                      name="anyoneCanHost"
                      id="anyoneCanHost"
                      color="primary"
                    />
                  }
                  label={t("menu.slot.anyoneCanHost")}
                  className={classes.checkboxLabel}
                />
              </Grid>

              <Grid item lg={12} xs={12}>
                <Select
                  id="host"
                  setValue={() => {}}
                  autoComplete={false}
                  value={slotCommitteeInformations?.hostName}
                  disabled
                  label={t("slots.presenter")}
                />
                {slotCommitteeInformations?.slotHosts?.length > 0 && (
                  <Grid item lg={6} xs={12} style={{ marginTop: 26 }}>
                    {slotCommitteeInformations?.slotHosts.map((host, index) => (
                      <Grid
                        item
                        lg={12}
                        xs={12}
                        key={`${host} - ${index}`}
                        style={{ marginBottom: 16 }}
                      >
                        <Chip label={host.userName} color="primary" />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item style={{ marginTop: "50px" }}>
              <Typography className={classes.subtitle}>
                {t("committees")}
              </Typography>
              <Grid container alignItems="center" spacing={3}>
                <Grid item lg={12} xs={12}>
                  <Select
                    id="committee"
                    autoComplete={false}
                    setValue={() => {}}
                    value={{}}
                    disabled
                    label={t("committees")}
                  />
                </Grid>

                {slotCommitteeInformations?.committees?.length > 0 && (
                  <Grid item lg={12} xs={12} style={{ marginTop: 15 }}>
                    {slotCommitteeInformations?.committees.map((item) => (
                      <CommitteCard
                        key={item.id}
                        name={item.nameCommittee}
                        onSetFinalElevation={() => {}}
                        finalElevation={item.finalAuthority}
                        onDelete={() => {}}
                      />
                    ))}
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      {minuteSlot && <MinuteDeliberationInfo minuteSlot={minuteSlot} />}
    </>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actionShowSpinner: (request: any) => dispatch(actionShowSpinner(request)),
});

export default compose<FC>(
  withRouter,
  withTranslation(),
  connect(null, mapDispatchToProps)
)(SlotCommitteeDetails);

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: "50px",
  },
  title: {
    ...theme.typography.h5,
  },
  subtitle: {
    font: "Inter",
    fontWeight: "bold",
    fontSize: "14.52px",
    color: "#4460BF",
    lineHeight: "17.57px",
  },
  checkboxLabel: {
    "& .MuiTypography-body1": {
      fontSize: "13px",
    },
  },
  keyboardDatePicker: {
    "& .MuiInput-underline:before": {
      borderBottomStyle: "solid",
      borderBottomColor: "#949494",
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      borderBottomStyle: "solid",
      borderBottomColor: "#949494",
    },
    "& .MuiInput-underline:after": {
      borderBottomStyle: "solid",
      borderBottomColor: "#000000",
    },
  },
}));
